/**
 * Which unit a figure is entered in — USDC or BTC, a collateral token, a
 * percentage of something.
 *
 * Generalised from the size field's USDC/base dropdown, which was a div with an
 * `onClick`: no role, no keyboard, nothing announced. The unit a number is in
 * is not decoration — reading "0.5" as USDC when it is BTC is a five-figure
 * mistake — so it is stated in the accessibility tree either way:
 *
 *  - two units  -> a radio group. The canonical pattern for "one of these": one
 *    tab stop, arrow keys between the options, the current one `aria-checked`.
 *  - three or more -> a listbox behind a button whose name carries both the
 *    field and the unit in force ("Size unit: USDC").
 *
 * Disabled is `aria-disabled`, not `disabled`: the control keeps its place in
 * the tab order so the unit can still be READ while an order is in flight.
 */
import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import {
  FIELD_COLORS,
  PRIMARY_COLORS,
  TEXT_COLORS,
} from "../../../constants/design/colors";
import { FONT_SIZE } from "../../../constants/design/fontSize";
import { BORDER_RADIUS, SPACING } from "../../../constants/design/spacing";
import { Chip } from "./style";
import {
  useRovingRadioGroup,
  wrapIndex,
} from "../../../keyboard/useRovingRadioGroup";

export interface UnitOption {
  id: string;
  label: string;
  icon?: React.ReactNode;
}

export interface UnitToggleProps {
  units: UnitOption[];
  value: string;
  onChange: (id: string) => void;
  disabled?: boolean;
  /** Names the control itself ("Size unit"), not the unit it currently holds. */
  ariaLabel: string;
}

/** Above this many units a row of segments stops fitting in a trade ticket. */
const SEGMENTED_UP_TO = 2;

const Group = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${SPACING.one}px;
`;

const Anchor = styled.div`
  position: relative;
  display: inline-flex;
`;

const IconSlot = styled.span`
  display: inline-flex;
  align-items: center;
  margin-right: ${SPACING.one}px;
`;

const Caret = styled.span`
  margin-left: ${SPACING.one}px;
  font-size: ${FONT_SIZE.xxs};
  color: ${TEXT_COLORS.three};
`;

const ListBox = styled.ul`
  position: absolute;
  top: calc(100% + ${SPACING.one}px);
  right: 0;
  z-index: 10;
  min-width: 100%;
  margin: 0;
  padding: ${SPACING.one}px;
  list-style: none;
  background: ${FIELD_COLORS.two};
  border-radius: ${BORDER_RADIUS.md}px;
  outline: none;
`;

const Option = styled.li<{ $active: boolean; $selected: boolean }>`
  display: flex;
  align-items: center;
  min-height: 32px;
  padding: 0 ${SPACING.two}px;
  border-radius: ${BORDER_RADIUS.sm}px;
  font-size: ${FONT_SIZE.one};
  white-space: nowrap;
  cursor: pointer;
  color: ${({ $selected }) => ($selected ? PRIMARY_COLORS.one : TEXT_COLORS.one)};
  background: ${({ $active }) => ($active ? PRIMARY_COLORS.four : "transparent")};
`;


export function UnitToggle({
  units,
  value,
  onChange,
  disabled,
  ariaLabel,
}: UnitToggleProps) {
  const selectedIndex = Math.max(
    0,
    units.findIndex((unit) => unit.id === value)
  );
  const current = units[selectedIndex];

  const segmented = units.length <= SEGMENTED_UP_TO;
  const segmentRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(selectedIndex);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);

  // Focus follows the open list, and comes back to the trigger when it shuts,
  // so the keyboard never lands on the page body mid-interaction.
  useEffect(() => {
    if (open) listRef.current?.focus();
  }, [open]);

  const pick = (index: number) => {
    if (disabled) return;
    const unit = units[index];
    if (unit) onChange(unit.id);
  };

  // The radiogroup keyboard contract, shared with SlippageControl and
  // SegmentedControl. `disabled` here is a whole-control flag, so every option
  // reports disabled: movement then finds nothing to land on and Enter/Space
  // selects nothing, which is what the hand-rolled version's `if (disabled)
  // return` did.
  const { itemProps: segmentA11y } = useRovingRadioGroup<string>({
    values: units.map((unit) => unit.id),
    value: current?.id ?? units[0]?.id ?? "",
    onSelect: (id) => onChange(id),
    focus: (_id, index) => segmentRefs.current[index]?.focus(),
    isDisabled: () => !!disabled,
  });

  const closeToTrigger = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  if (segmented) {
    return (
      <Group role="radiogroup" aria-label={ariaLabel}>
        {units.map((unit, index) => {
          const checked = index === selectedIndex;
          return (
            <Chip
              key={unit.id}
              ref={(node: HTMLButtonElement | null) => {
                segmentRefs.current[index] = node;
              }}
              type="button"
              {...segmentA11y(unit.id, index)}
              $active={checked}
              onClick={() => pick(index)}
            >
              {unit.icon ? <IconSlot aria-hidden="true">{unit.icon}</IconSlot> : null}
              {unit.label}
            </Chip>
          );
        })}
      </Group>
    );
  }

  const optionId = (index: number) => `${ariaLabel.replace(/\s+/g, "-")}-unit-${units[index]?.id}`;

  return (
    <Anchor>
      <Chip
        ref={triggerRef}
        type="button"
        // Both halves in the name: the field, and the unit in force. The visible
        // text is the unit, so the name still contains it (WCAG 2.5.3).
        aria-label={`${ariaLabel}: ${current?.label ?? ""}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-disabled={disabled || undefined}
        $active={open}
        onClick={() => {
          if (disabled) return;
          setActiveIndex(selectedIndex);
          setOpen((wasOpen) => !wasOpen);
        }}
        onKeyDown={(event: React.KeyboardEvent<HTMLButtonElement>) => {
          if (disabled) return;
          if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            setActiveIndex(selectedIndex);
            setOpen(true);
          }
        }}
      >
        {current?.icon ? <IconSlot aria-hidden="true">{current.icon}</IconSlot> : null}
        {current?.label}
        <Caret aria-hidden="true">▾</Caret>
      </Chip>

      {open ? (
        <ListBox
          ref={listRef}
          role="listbox"
          aria-label={ariaLabel}
          aria-activedescendant={optionId(activeIndex)}
          tabIndex={-1}
          onBlur={() => setOpen(false)}
          onKeyDown={(event: React.KeyboardEvent<HTMLUListElement>) => {
            if (event.key === "ArrowDown") {
              event.preventDefault();
              setActiveIndex((index) => wrapIndex(index + 1, units.length));
            } else if (event.key === "ArrowUp") {
              event.preventDefault();
              setActiveIndex((index) => wrapIndex(index - 1, units.length));
            } else if (event.key === "Home") {
              event.preventDefault();
              setActiveIndex(0);
            } else if (event.key === "End") {
              event.preventDefault();
              setActiveIndex(units.length - 1);
            } else if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              pick(activeIndex);
              closeToTrigger();
            } else if (event.key === "Escape" || event.key === "Tab") {
              event.preventDefault();
              closeToTrigger();
            }
          }}
        >
          {units.map((unit, index) => (
            <Option
              key={unit.id}
              id={optionId(index)}
              role="option"
              aria-selected={index === selectedIndex}
              $active={index === activeIndex}
              $selected={index === selectedIndex}
              onMouseDown={(event: React.MouseEvent) => event.preventDefault()}
              onClick={() => {
                pick(index);
                closeToTrigger();
              }}
            >
              {unit.icon ? <IconSlot aria-hidden="true">{unit.icon}</IconSlot> : null}
              {unit.label}
            </Option>
          ))}
        </ListBox>
      ) : null}
    </Anchor>
  );
}
